import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { ProductDimension } from '../../../modules/productsListing/types'
import { useStyles } from './DimensionRows.styles'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { get, isEmpty } from 'lodash'
import { getRandom, preventEventForKeys } from '@labrav/common'
import { Price, PriceTypes } from '../../../modules/privateOffer/types'
import { useCommonStyles } from '../../../common/styles/common.styles'
import { getStandardDimensionPriceMappings } from './utils'

export interface DimensionsData {
  quantity?: number
  id: string
  price?: Price
}

export type DimensionRowsProps = {
  offerDimension: DimensionsData
  offerIds: string[]
  pricesArray?: [keyof PriceTypes]
  productDimensions: ProductDimension[]
  updateId: (event: SelectChangeEvent, child: React.ReactNode) => void
  updateQuantity: (event: React.ChangeEvent<HTMLInputElement>) => void
  deleteRow: () => void
  index: number
  isCustom?: boolean
  isFlexiblePayment?: boolean
  isFreeTrial?: boolean
  errors: Record<string, unknown>
}

export const DimensionRows = React.memo(
  ({
    index,
    isCustom,
    offerDimension,
    productDimensions,
    updateId,
    updateQuantity,
    deleteRow,
    offerIds,
    errors,
    isFlexiblePayment = true,
    pricesArray = ['price'],
  }: DimensionRowsProps) => {
    const { classes } = useStyles()
    const { classes: commonClasses } = useCommonStyles()
    const [quantityValue, setQuantityValue] = useState(
      offerDimension?.quantity ?? ''
    )

    const onHandleChangeQuantity = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const dataValue = `${Math.round(Number(event.target.value))}`
      setQuantityValue(dataValue)
      updateQuantity({
        ...event,
        target: {
          ...event.target,
          value: dataValue,
          id: event.target.id,
        },
      })
    }
    const dimension = productDimensions.find(
      dimension => dimension.id === offerDimension.id
    )
    const mappings: Record<string, string> = useMemo(
      () => getStandardDimensionPriceMappings(isCustom),
      [isCustom]
    )
    const randomKey = useMemo(getRandom, [
      index,
      offerIds.length,
      offerDimension.id,
    ])
    return (
      <div
        className={classes.row}
        data-testid="dimension-row"
        key={`${randomKey}-${index}`}
      >
        <FormControl className={classes.formWidth}>
          <InputLabel
            id="product-dimension-label"
            className={classes.inputLabel}
            classes={{ shrink: commonClasses.shrink }}
          >
            Name
          </InputLabel>
          <Select
            labelId="product-dimension"
            defaultValue={dimension?.id ?? ''}
            onChange={updateId}
            label="Name"
            inputProps={{ 'data-testid': 'input' }}
            className={classes.selectField}
            variant="standard"
            IconComponent={props => (
              <KeyboardArrowDownIcon
                {...props}
                fontSize="small"
                data-testid="arrow-button"
              />
            )}
            classes={{ icon: classes.icon }}
            data-testid="dimension-name"
          >
            {productDimensions.map(dimension => (
              <MenuItem
                data-testid="menu-item"
                value={dimension.id}
                className={classes.menuRoot}
                disabled={offerIds.includes(dimension?.id || '')}
                key={dimension.id}
              >
                {dimension.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id="description"
          label="Description"
          variant="standard"
          value={dimension?.labelDescription || '--'}
          className={clsx(classes.selectField, classes.description)}
          data-testid="description"
          multiline
          slotProps={{
            input: {
              readOnly: true,
              disableUnderline: true,
            },
          }}
        />
        <div
          className={clsx(classes.quantityFieldContainer, classes.textField)}
        >
          {isFlexiblePayment ? (
            <TextField
              key="quantity"
              id="quantity"
              name="quantity"
              label="QUANTITY"
              variant="standard"
              required={true}
              defaultValue={quantityValue}
              value={quantityValue}
              className={clsx(classes.selectField, classes.textField)}
              disabled={isEmpty(dimension?.id)}
              onChange={onHandleChangeQuantity}
              type="number"
              onKeyDown={preventEventForKeys(['+', '-', '.', 'e'])}
              error={!isEmpty(errors?.quantity)}
              helperText={(errors?.quantity as string) || ''}
              slotProps={{
                input: { inputProps: { min: 1, 'data-testid': 'quantity' } },
              }}
            />
          ) : (
            pricesArray.map(price => (
              <TextField
                key={price}
                id={price}
                name={price}
                label={mappings[price]}
                variant="standard"
                required={true}
                defaultValue={
                  get(offerDimension, `price.${price}`, null) ?? null
                }
                className={clsx(
                  classes.selectField,
                  classes.priceFieldsWidthAndSpacing
                )}
                disabled={isEmpty(dimension?.id)}
                onChange={updateQuantity}
                type="number"
                onKeyDown={preventEventForKeys(['+', '-', 'e'])}
                error={!isEmpty(errors?.[price])}
                helperText={(errors?.[price] as string) || ''}
                slotProps={{
                  input: {
                    inputProps: { min: 1, 'data-testid': `price-${price}` },
                  },
                }}
              />
            ))
          )}
          <IconButton
            className={classes.iconButton}
            onClick={deleteRow}
            data-testid="cancel-button"
            size="large"
          >
            <HighlightOffIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
    )
  }
)
